import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { Plus, CheckCircle, AlertTriangle, ChevronRight } from 'lucide-react';
import useArrowNav from '../../hooks/useArrowNav';
import {
  Badge, emptyMsg, cardWrap, btnPrimary, inp,
  TASK_PRIORITY, neuShadow, neuShadowSm
} from './crmShared';
import CrmFilterBar from '../../components/CrmFilterBar/CrmFilterBar';

/* ═══════════════════════════════════════════════════════════════════════════
   TasksTab — Collapsible accordion grouped by task status
   • Task #230: owns its data — fetches via crm:filtered-list
   • Tasks grouped by status (pending, in_progress, completed, cancelled)
   ═══════════════════════════════════════════════════════════════════════════ */

export default function TasksTab({
  allUsers, currentUser, canCreate, setShowAddTask,
  completeTask, onContactClick,
  /*
    ⛔ BUMPED BY CRMPage WHEN A TASK IS CREATED — Card CONTACT-PROFILE-INTAKE
    item 12. This tab owns its data, so nothing the parent reloads can make a
    new task appear here. The counter is the only wire between "a task was
    created" and "the list the rep is looking at is refetched".
  */
  taskListVersion = 0,
}) {
  // ── Own data state (Task #230) ────────────────────────────────────────
  const [taskList, setTaskList] = useState([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);

  useEffect(() => {
    if (!activeFilter) return;
    let cancelled = false;
    setListLoading(true);
    window.zignagenixAPI.getCRMFilteredList({ resource: 'tasks', ...activeFilter })
      .then(res => {
        if (!cancelled) {
          setTaskList(Array.isArray(res?.rows) ? res.rows : []);
          setTotal(res?.total || 0);
        }
      })
      .catch(() => { if (!cancelled) setTaskList([]); })
      .finally(() => { if (!cancelled) setListLoading(false); });
    return () => { cancelled = true; };
  }, [activeFilter, taskListVersion]);

  // ── State ────────────────────────────────────────────────────────────────
  const [expandedStatuses, setExpandedStatuses] = useState(new Set());
  const taskListRef = useRef(null);
  const { onKeyDown: handleTasksKeyDown } = useArrowNav(taskListRef, '[data-task-item]', { horizontal: false, vertical: true });

  // ── Status configuration ─────────────────────────────────────────────────
  const statusConfig = {
    pending: { label: 'Pending', color: '#F26522' },
    in_progress: { label: 'In Progress', color: '#2E8B98' },
    completed: { label: 'Completed', color: '#62BB46' },
    cancelled: { label: 'Cancelled', color: '#ADB5BD' },
  };
  const statusOrder = ['pending', 'in_progress', 'completed', 'cancelled'];

  const toggleStatus = useCallback((status) => {
    setExpandedStatuses(prev => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => setExpandedStatuses(new Set(statusOrder)), []);
  const collapseAll = useCallback(() => setExpandedStatuses(new Set()), []);

  // Group task list by status (data already filtered server-side)
  const grouped = useMemo(() => {
    const groups = { pending: [], in_progress: [], completed: [], cancelled: [] };
    taskList.forEach(t => {
      if (groups[t.status]) groups[t.status].push(t);
      else groups.pending.push(t);
    });
    return groups;
  }, [taskList]);

  // Auto-expand pending + in_progress on first load
  useEffect(() => {
    if (taskList.length > 0 && expandedStatuses.size === 0) {
      setExpandedStatuses(new Set(['pending', 'in_progress']));
    }
  }, [taskList]);

  return (
    <>
      {/* ═══ Unified Filter Bar (Task #230) ═══ */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
        <CrmFilterBar
          resource="tasks"
          allUsers={allUsers}
          currentUser={currentUser}
          onFilterChange={setActiveFilter}
        />
        {canCreate && (
          <button onClick={() => setShowAddTask(true)} style={{ ...btnPrimary, background: '#F26522', flexShrink: 0 }}>
            <Plus size={14} /> Add Task
          </button>
        )}
      </div>

      {/* ═══ Stats + Expand/Collapse Controls ═══ */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 10, padding: '0 2px',
      }}>
        <div style={{ fontSize: 12, color: '#6C757D' }}>
          {listLoading
            ? <span>Loading…</span>
            : <><strong style={{ color: '#212529' }}>{total}</strong> task{total !== 1 ? 's' : ''}</>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={expandAll} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#2E8B98', fontWeight: 600 }}>Expand All</button>
          <span style={{ color: '#DEE2E6' }}>|</span>
          <button onClick={collapseAll} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#2E8B98', fontWeight: 600 }}>Collapse All</button>
        </div>
      </div>

      {/* ═══ Task Groups by Status ═══ */}
      <div ref={taskListRef} onKeyDown={handleTasksKeyDown} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {!listLoading && taskList.length === 0 ? (
          <div style={emptyMsg}>{activeFilter ? 'No tasks match your filters' : 'No tasks found'}</div>
        ) : (
          statusOrder.map(status => {
            const statusTasks = grouped[status];
            const config = statusConfig[status];
            if (!statusTasks || statusTasks.length === 0) return null;

            return (
              <StatusGroup
                key={status}
                status={status}
                config={config}
                tasks={statusTasks}
                isExpanded={expandedStatuses.has(status)}
                onToggle={() => toggleStatus(status)}
                canCreate={canCreate}
                completeTask={completeTask}
                onContactClick={onContactClick}
              />
            );
          })
        )}
      </div>
    </>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   Single status group accordion
   ═══════════════════════════════════════════════════════════════════════════ */

function StatusGroup({
  status, config, tasks, isExpanded, onToggle, canCreate, completeTask, onContactClick
}) {
  const groupRef = useRef(null);
  const { onKeyDown: handleGroupKeyDown } = useArrowNav(groupRef, '[data-task-item]', { horizontal: false, vertical: true });

  return (
    <div
      id={`task-group-${status}`}
      style={{
        background: '#F8F9FA', borderRadius: 12, boxShadow: neuShadowSm,
        overflow: 'hidden', transition: 'all .2s ease',
        border: isExpanded ? `2px solid ${config.color}` : '2px solid transparent',
      }}
    >
      {/* ─── Status Header Button ─── */}
      <button
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 12, width: '100%',
          padding: '12px 16px', border: 'none', cursor: 'pointer', textAlign: 'left',
          background: isExpanded ? `${config.color}08` : '#F8F9FA',
          borderRadius: isExpanded ? '10px 10px 0 0' : 10,
          transition: 'background .15s ease',
        }}
        onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#f0f4f8'; }}
        onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = '#F8F9FA'; }}
      >
        {/* Chevron indicator */}
        <ChevronRight
          size={18}
          color={isExpanded ? config.color : '#6C757D'}
          style={{
            transition: 'transform .25s ease',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            flexShrink: 0,
          }}
        />

        {/* Status name + count badge */}
        <span style={{
          fontSize: 15, fontWeight: 700, color: isExpanded ? config.color : '#212529',
        }}>
          {config.label}
        </span>
        <div style={{ flex: 1 }} />
        <Badge text={`${tasks.length}`} color={config.color} />
      </button>

      {/* ─── Collapsible Task List ─── */}
      <div style={{
        maxHeight: isExpanded ? tasks.length * 80 + 20 : 0,
        opacity: isExpanded ? 1 : 0,
        overflow: 'hidden',
        transition: 'max-height .35s ease, opacity .25s ease',
      }}>
        <div ref={groupRef} onKeyDown={handleGroupKeyDown} style={{ padding: '0 4px 4px' }}>
          {tasks.map((t, i) => (
            <TaskRow
              key={t.id}
              task={t}
              index={i}
              canCreate={canCreate}
              completeTask={completeTask}
              onContactClick={onContactClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   Single task row
   ═══════════════════════════════════════════════════════════════════════════ */

function TaskRow({ task: t, index: i, canCreate, completeTask, onContactClick }) {
  const pr = TASK_PRIORITY[t.priority] || TASK_PRIORITY.normal;
  const isOverdue = t.due_date && t.status !== 'completed' && new Date(t.due_date) < new Date();

  return (
    <div
      data-task-item
      tabIndex={0}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
        borderBottom: 'none', background: i % 2 === 0 ? '#fff' : '#F8F9FA',
        borderRadius: 8, marginBottom: 2, outline: 'none', transition: 'background .1s ease',
      }}
      onFocus={e => { e.currentTarget.style.backgroundColor = '#dce2e8'; }}
      onBlur={e => { e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#fff' : '#F8F9FA'; }}
    >
      {/* Checkbox / Complete button */}
      {t.status === 'completed' ? (
        <CheckCircle size={20} color="#62BB46" style={{ flexShrink: 0 }} />
      ) : canCreate ? (
        <button
          onClick={() => completeTask(t.id)}
          style={{
            background: 'none', border: '2px solid #cbd5e1', borderRadius: 4,
            width: 20, height: 20, cursor: 'pointer', flexShrink: 0,
          }}
          title="Mark complete"
        />
      ) : (
        <button
          disabled
          style={{
            background: 'none', border: '2px solid #cbd5e1', borderRadius: 4,
            width: 20, height: 20, cursor: 'not-allowed', flexShrink: 0, opacity: 0.5,
          }}
          title="No permission"
        />
      )}

      {/* Title + contact/assigned info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600, fontSize: 13,
            color: t.status === 'completed' ? '#ADB5BD' : '#212529',
            textDecoration: t.status === 'completed' ? 'line-through' : 'none',
          }}
        >
          {t.title}
        </div>
        <div style={{ fontSize: 11, color: '#6C757D' }}>
          {t.contact_first ? (
            t.contact_id ? (
              <span onClick={() => onContactClick && onContactClick(t.contact_id)} style={{ cursor: 'pointer', color: '#2E8B98', fontWeight: 600 }}>
                {t.contact_first} {t.contact_last}
              </span>
            ) : (
              <span>{t.contact_first} {t.contact_last}</span>
            )
          ) : t.patient_first ? (
            t.patient_id ? (
              <span onClick={() => onContactClick && onContactClick(t.patient_id)} style={{ cursor: 'pointer', color: '#2E8B98', fontWeight: 600 }}>
                {t.patient_first} {t.patient_last}
              </span>
            ) : (
              <span>{t.patient_first} {t.patient_last}</span>
            )
          ) : null}
          {(t.contact_first || t.patient_first) && t.assigned_to_name && <span> — {t.assigned_to_name}</span>}
          {!(t.contact_first || t.patient_first) && t.assigned_to_name && <span>{t.assigned_to_name}</span>}
        </div>
      </div>

      {/* Priority badge */}
      <Badge text={pr.label} color={pr.color} bg={pr.bg} />

      {/* Due date + overdue indicator */}
      <div
        style={{
          fontSize: 11, color: isOverdue ? '#DC3545' : '#ADB5BD',
          fontWeight: isOverdue ? 600 : 400, width: 80, textAlign: 'right',
          flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4,
        }}
      >
        {isOverdue && <AlertTriangle size={10} />}
        {t.due_date || '—'}
      </div>
    </div>
  );
}
